import { useEffect, useState, useCallback, useMemo } from "react";
import { removeNetworkedObject } from "../../utils/removeNetworkedObject";
import { rotateInPlaceAroundWorldUp, affixToWorldUp } from "../../utils/three-utils";
import { getPromotionTokenForFile } from "../../utils/media-utils";
import { hasComponent } from "bitecs";
import { Lockeable, Locked, Pinnable, Pinned, Static } from "../../bit-components";
import mintNft from "../../mintNft";
import { useRef } from "react";
import { guessContentType } from "../../utils/media-url-utils";
import configs from "../../utils/configs";
import { isMirrorMedia } from "../../utils/object-menu-mirror-utils";

function getPinnedState(el) {
  return !!(hasComponent(APP.world, Pinnable, el.eid) && hasComponent(APP.world, Pinned, el.eid));
}

function getLockedState(el) {
  return !!(hasComponent(APP.world, Lockeable, el.eid) && hasComponent(APP.world, Locked, el.eid));
}

export function isMe(object) {
  return object.el.id === "avatar-rig";
}

export function isPlayer(object) {
  return !!object.el.components["networked-avatar"] || !!object.el.components["avatar-animation"];
}

export function getObjectUrl(object) {
  const mediaLoader = object.el.components["media-loader"];

  const url =
    mediaLoader && ((mediaLoader.data.mediaOptions && mediaLoader.data.mediaOptions.href) || mediaLoader.data.src);

  if (url && !url.startsWith("hubs://")) {
    return url;
  }

  return null;
}

export function useLockedObject(hubChannel, scene, object) {
  const [isLocked, setIsLocked] = useState(getLockedState(object.el));
  const mediaLoader = object.el.components["media-loader"];
  const url =
    mediaLoader && ((mediaLoader.data.mediaOptions && mediaLoader.data.mediaOptions.href) || mediaLoader.data.src);

  const [networkedEl, setNetworkedEl] = useState();
  const [canLockObjects, setCanLockObjects] = useState(true);
  const contentType = guessContentType(url);
  const contentSubtype = object.el.components["media-loader"].data.contentSubtype ?? "";

  useEffect(() => {
    NAF.utils.getNetworkedEntity(object.el).then(response => {
      setNetworkedEl(response);
    });
  }, [object]);

  useEffect(() => {
    const isMine = object.el.hasAttribute("emit-scene-event-on-remove");
    let canLockObjects = contentType === "video/vnd.hubs-webrtc" ? isMine || configs.isAdmin() : true;

    setCanLockObjects(canLockObjects);
  }, [contentType, contentSubtype]);

  useEffect(() => {
    const el = object.el;

    function onLockStateChanged() {
      let lockedStateToSet = getLockedState(el);
      setIsLocked(lockedStateToSet);
    }
    el.addEventListener("locked", onLockStateChanged);
    el.addEventListener("unlocked", onLockStateChanged);
    setIsLocked(getLockedState(el));
    return () => {
      el.removeEventListener("locked", onLockStateChanged);
      el.removeEventListener("unlocked", onLockStateChanged);
    };
  }, [object]);

  return { isLocked, canLockObjects };
}

export function usePinObject(hubChannel, scene, object) {
  const [isPinned, setIsPinned] = useState(object ? getPinnedState(object.el) : null);

  const pinObject = useCallback(() => {
    const el = object.el;
    if (!NAF.utils.isMine(el) && !NAF.utils.takeOwnership(el)) return;
    window.APP.pinningHelper.setPinned(el, true, true);
  }, [object]);

  const unpinObject = useCallback(() => {
    const el = object.el;
    if (!NAF.utils.isMine(el) && !NAF.utils.takeOwnership(el)) return;
    window.APP.pinningHelper.setPinned(el, false, true);
  }, [object]);

  const togglePinned = useCallback(() => {
    if (isPinned) {
      unpinObject();
    } else {
      pinObject();
    }
  }, [isPinned, pinObject, unpinObject]);

  useEffect(() => {
    const el = object?.el;

    if (!object) {
      return;
    }

    function onPinStateChanged() {
      setIsPinned(getPinnedState(el));
    }
    el.addEventListener("pinned", onPinStateChanged);
    el.addEventListener("unpinned", onPinStateChanged);
    setIsPinned(getPinnedState(el));
    return () => {
      el.removeEventListener("pinned", onPinStateChanged);
      el.removeEventListener("unpinned", onPinStateChanged);
    };
  }, [object]);

  const el = object?.el;

  let userOwnsFile = false;

  const { fileIsOwned, fileId } = el?.components["media-loader"]?.data || {};

  if (el?.components["media-loader"]) {
    userOwnsFile = fileIsOwned || (fileId && getPromotionTokenForFile(fileId));
  }

  const canPin =
    object &&
    !!(
      scene.is("entered") &&
      !isPlayer(object) &&
      !hasComponent(APP.world, Static, el.eid) &&
      hubChannel.can("pin_objects") &&
      userOwnsFile
    );

  return { canPin, isPinned, togglePinned, pinObject, unpinObject };
}

export function useGoToSelectedObject(scene, object) {
  const goToSelectedObject = useCallback(() => {
    const viewingCamera = document.getElementById("viewing-camera");
    const targetMatrix = new THREE.Matrix4();
    const translation = new THREE.Matrix4();
    viewingCamera.object3DMap.camera.updateMatrices();
    targetMatrix.copy(viewingCamera.object3DMap.camera.matrixWorld);
    affixToWorldUp(targetMatrix, targetMatrix);
    translation.makeTranslation(0, -1.6, 0.15);
    targetMatrix.multiply(translation);
    rotateInPlaceAroundWorldUp(targetMatrix, Math.PI, targetMatrix);

    scene.systems["hubs-systems"].characterController.enqueueWaypointTravelTo(targetMatrix, true, {
      willDisableMotion: false,
      willDisableTeleporting: false,
      snapToNavMesh: false,
      willMaintainInitialOrientation: false
    });
  }, [scene]);

  const uiRoot = useMemo(() => document.getElementById("ui-root"), []);
  const isSpectating = uiRoot && uiRoot.classList.contains("isGhost");
  const canGoTo = !isSpectating && !isPlayer(object);

  return { canGoTo, goToSelectedObject };
}

export function useRemoveObject(hubChannel, scene, object) {
  const removeObject = useCallback(() => {
    removeNetworkedObject(scene, object.el);
  }, [scene, object]);

  const el = object.el;

  const canRemoveObject = !!(
    scene.is("entered") &&
    !isPlayer(object) &&
    !getPinnedState(el) &&
    !getLockedState(el) &&
    !hasComponent(APP.world, Static, el.eid) &&
    hubChannel.can("spawn_and_move_media") &&
    !isMirrorMedia(el.parentEl)
  );

  return { removeObject, canRemoveObject };
}

export function useMintObject(object) {
  const url = getObjectUrl(object);
  const contentType = guessContentType(url);
  const [networkedEl, setNetworkedEl] = useState();
  const [canMintObject, setCanMintObject] = useState(false);
  const contentSubtype = object.el.components["media-loader"].data.contentSubtype ?? "";

  useEffect(() => {
    NAF.utils.getNetworkedEntity(object.el).then(response => {
      setNetworkedEl(response);
    });
  }, [object]);

  useEffect(() => {
    setCanMintObject(
      networkedEl &&
        NAF.utils.isMine(networkedEl) &&
        contentType &&
        contentType !== "video/vnd.hubs-webrtc" &&
        contentSubtype !== "message" &&
        configs.NFT_MARKETPLACE_URL
    );
  }, [networkedEl, contentType, contentSubtype]);

  const isMintingObject = useRef(false);
  const mintObject = useCallback(async () => {
    isMintingObject.current = true;
    await mintNft(url);
    isMintingObject.current = false;
  }, [isMintingObject, url]);

  return { mintObject, canMintObject, isMintingObject };
}

export function usePlaceartEdit(hubChannel, scene, object) {
  const el = object.el;

  const qrLoading = el.querySelector("[artwork-qr] [media-loader]");

  let userOwnsFile = false;
  let fileIsOwned, fileId;

  let mediaLoaderData = el.components["media-loader"]?.data;
  if (mediaLoaderData) {
    fileIsOwned = mediaLoaderData?.fileIsOwned;
    fileId = mediaLoaderData?.fileId;
  }

  if (el.components["media-loader"]) {
    userOwnsFile = fileIsOwned || (fileId && getPromotionTokenForFile(fileId));
  }

  const canEditArtwork = !!(
    scene.is("entered") &&
    !isPlayer(object) &&
    !getPinnedState(el) &&
    !hasComponent(APP.world, Static, el.eid) &&
    hubChannel.can("spawn_and_move_media") &&
    !isMirrorMedia(el.parentEl) &&
    userOwnsFile
  );

  const canBeAnArtwork = !!(
    scene.is("entered") &&
    !isPlayer(object) &&
    !getPinnedState(el) &&
    !hasComponent(APP.world, Static, el.eid) &&
    hubChannel.can("spawn_and_move_media") &&
    !isMirrorMedia(el.parentEl) &&
    userOwnsFile &&
    !el?.components["artwork"] &&
    (el?.components["media-image"] || el?.components["gltf-model-plus"])
  );

  const [placeartOpen, setPlaceartOpen] = useState(false);

  const [artworkReady, setArtworkReady] = useState(!qrLoading);

  const [artworkModalOpen, setArtworkModalOpen] = useState(false);

  useEffect(() => {
    const el = object.el;

    function loadQR() {
      setArtworkReady(false);
    }

    function loadedQR() {
      setArtworkReady(true);
    }
    el.addEventListener("load-qr", loadQR);
    el.addEventListener("loaded-qr", loadedQR);
    return () => {
      el.removeEventListener("load-qr", loadQR);
      el.removeEventListener("loaded-qr", loadedQR);
    };
  }, [object]);

  const togglePlaceart = () => {
    setPlaceartOpen(!placeartOpen);
  };

  const closePlaceart = () => {
    setPlaceartOpen(false);
  };

  const toggleArtworkModal = () => {
    setArtworkModalOpen(!artworkModalOpen);
  };

  return {
    canEditArtwork,
    placeartOpen,
    togglePlaceart,
    closePlaceart,
    artworkReady,
    canBeAnArtwork,
    toggleArtworkModal,
    artworkModalOpen
  };
}

export function useArtworkObject(hubChannel, scene, object) {
  const el = object.el;

  const canEditArtwork = !!(
    scene.is("entered") &&
    !isPlayer(object) &&
    !getPinnedState(el) &&
    !hasComponent(APP.world, Static, el.eid) &&
    hubChannel.can("spawn_and_move_media") &&
    !isMirrorMedia(el.parentEl)
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return { canEditArtwork, sidebarOpen, toggleSidebar, closeSidebar };
}

export function useHideAvatar(hubChannel, avatarEl) {
  const hideAvatar = useCallback(() => {
    if (avatarEl.components.networked) {
      const clientId = avatarEl.components.networked.data.owner;

      if (clientId && clientId !== NAF.clientId) {
        hubChannel.hide(clientId);
      }
    }
  }, [hubChannel, avatarEl]);

  return hideAvatar;
}

export function useShowAvatar(hubChannel, avatarEl) {
  const showAvatar = useCallback(() => {
    if (avatarEl.components.networked) {
      const clientId = avatarEl.components.networked.data.owner;

      if (clientId && clientId !== NAF.clientId) {
        hubChannel.unhide(clientId);
      }
    }
  }, [hubChannel, avatarEl]);

  return showAvatar;
}
